import { runBoundedCompletion } from "../autonomy/bounded-completion.ts";
import type { RouteDecision } from "../autonomy/contracts.ts";
import { parseModelOutputJsonObject } from "../model-output-json.ts";
import { ROUTE_JUDGE_SYSTEM_PROMPT } from "../provider-prompt-contracts.ts";

export { ROUTE_JUDGE_SYSTEM_PROMPT };

/**
 * Routing-only judge: a bounded, tool-less completion (default: the medium model) that decides the
 * final cheap/medium/expensive tier for a user prompt, refining the regex classifier's baseline.
 * Core rule: planning is never cheap unless the judge explicitly deems the task trivial. The judge
 * proposes; the existing pipeline (model resolution, auth, escalation, gates) still decides.
 * Failure is honest: unparseable/timeout/unavailable falls back to the baseline with a visible
 * reasonCode, never silently.
 */

/** Static across calls — callers pass cacheRetention "short" so only the variable tail is billed. */
export const ROUTE_JUDGE_MAX_OUTPUT_TOKENS = 128;
export const ROUTE_JUDGE_MAX_WALL_CLOCK_MS = 10_000;

export interface RouteJudgeVerdict {
	tier: "cheap" | "medium" | "expensive";
	risk: RouteDecision["risk"];
	trivial: boolean;
	reason: string;
}

export function buildRouteJudgeUserPrompt(args: { prompt: string; baseline: RouteDecision }): string {
	return [
		`BASELINE tier=${args.baseline.tier} risk=${args.baseline.risk} reason=${args.baseline.reasonCode}`,
		"USER PROMPT",
		args.prompt.slice(0, 4000),
	].join("\n");
}

const JUDGE_TIERS: readonly string[] = ["cheap", "medium", "expensive"];
const JUDGE_RISKS: readonly string[] = ["read-only", "scoped-write", "high-impact", "approval-required"];

export function parseRouteJudgeVerdict(text: string): RouteJudgeVerdict | undefined {
	const record = parseModelOutputJsonObject(text);
	if (!record) return undefined;
	// The judge may never select the learning tier or anything outside the three foreground tiers.
	if (typeof record.tier !== "string" || !JUDGE_TIERS.includes(record.tier)) return undefined;
	const risk =
		typeof record.risk === "string" && JUDGE_RISKS.includes(record.risk)
			? (record.risk as RouteDecision["risk"])
			: undefined;
	if (!risk) return undefined;
	return {
		tier: record.tier as RouteJudgeVerdict["tier"],
		risk,
		trivial: record.trivial === true,
		reason: typeof record.reason === "string" ? record.reason.slice(0, 200) : "",
	};
}

/** Merge a judge verdict into the baseline decision (pure; never returns learning). */
export function applyRouteJudgeVerdict(baseline: RouteDecision, verdict: RouteJudgeVerdict): RouteDecision {
	// Enforce the core rule in code, not just in the judge prompt: downgrading a non-cheap
	// baseline to cheap requires an EXPLICIT trivial verdict. An untrusted judge saying
	// {tier:"cheap", trivial:false} for an elevated prompt keeps the baseline tier.
	const tier =
		verdict.tier === "cheap" && baseline.tier !== "cheap" && !verdict.trivial ? baseline.tier : verdict.tier;
	return {
		...baseline,
		tier,
		risk: verdict.risk,
		confidence: Math.max(baseline.confidence, 0.75),
		reasonCode: `judge_${tier}${verdict.trivial ? "_trivial" : ""}`,
		reasons: [...baseline.reasons, `Route judge: ${verdict.reason || "no reason given"}`],
	};
}

export interface RouteJudgeRunResult {
	decision: RouteDecision;
	verdict?: RouteJudgeVerdict;
	/** Set when the judge could not decide and the baseline was kept. */
	fallbackReason?: string;
	costUsd: number;
}

function judgeFallbackDecision(baseline: RouteDecision, reason: string): RouteDecision {
	return {
		...baseline,
		reasonCode: "judge_unavailable_fallback",
		reasons: [...baseline.reasons, reason],
	};
}

/**
 * Run the judge over a baseline decision. The completion executor is injected (production:
 * AgentSession.runIsolatedCompletion on the judge model). Never throws; every failure keeps the
 * baseline with a visible fallbackReason.
 */
export async function runRouteJudge(args: {
	prompt: string;
	baseline: RouteDecision;
	complete: (input: { systemPrompt: string; userPrompt: string; signal?: AbortSignal }) => Promise<{
		text: string;
		costUsd: number;
		stopReason: string;
	}>;
	signal?: AbortSignal;
	maxWallClockMs?: number;
}): Promise<RouteJudgeRunResult> {
	const bounded = await runBoundedCompletion({
		maxWallClockMs: args.maxWallClockMs ?? ROUTE_JUDGE_MAX_WALL_CLOCK_MS,
		signal: args.signal,
		execute: (signal) =>
			args.complete({
				systemPrompt: ROUTE_JUDGE_SYSTEM_PROMPT,
				userPrompt: buildRouteJudgeUserPrompt({ prompt: args.prompt, baseline: args.baseline }),
				signal,
			}),
	});
	const costUsd = bounded.completion?.costUsd ?? 0;

	if (bounded.failure || !bounded.completion) {
		return {
			decision: judgeFallbackDecision(args.baseline, "Route judge unavailable; baseline kept"),
			fallbackReason: bounded.failure ? `judge_${bounded.failure.reasonCode}` : "judge_unavailable_fallback",
			costUsd,
		};
	}
	if (bounded.completion.stopReason === "error" || bounded.completion.stopReason === "aborted") {
		return {
			decision: judgeFallbackDecision(args.baseline, "Route judge errored; baseline kept"),
			fallbackReason: "judge_model_error",
			costUsd,
		};
	}

	const verdict = parseRouteJudgeVerdict(bounded.completion.text);
	if (!verdict) {
		return {
			decision: judgeFallbackDecision(args.baseline, "Route judge unparseable; baseline kept"),
			fallbackReason: "judge_unparseable_fallback",
			costUsd,
		};
	}

	return { decision: applyRouteJudgeVerdict(args.baseline, verdict), verdict, costUsd };
}
